import { type ChangeEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useProfileManagement from '@/domains/profile-management/hooks/useProfileManagement';
import type { PasswordUpdateData } from '@/domains/profile-management/types/profile-management.types';
import NoticeMessage from '@/plugin/noticemessage/noticemessage';
// still use this path,
import Button from '@/shared/components/ui/Button';
import DetailModal from '@/shared/components/ui/Modal/DetailModal/DetailModal';
import Common from '@/shared/utils/standard';

interface UpdatePasswordProps {
  isActive?: boolean;
  userInfo: string;
}

interface FormData {
  account_id: string;
  current_password: string;
  no_hash_password_update: string;
  no_hash_confirm_password_update: string;
  current_password_sha256?: string;
  confirm_password_update_sha256?: string;
}

const UpdatePassword = ({ isActive = true, userInfo }: UpdatePasswordProps) => {
  const { t } = useTranslation();

  const [isDisabled, setIsDisabled] = useState(true);
  const [isRequired, setIsRequired] = useState(false);

  const [formData, setFormData] = useState<FormData>({
    account_id: userInfo,
    current_password: '',
    no_hash_password_update: '',
    no_hash_confirm_password_update: '',
  });

  const { updateProfilePassword } = useProfileManagement({
    onUpdateSuccess: () => {
      setIsDisabled(true);
      setIsRequired(false);
      setFormData({
        account_id: userInfo,
        current_password: '',
        no_hash_password_update: '',
        no_hash_confirm_password_update: '',
        current_password_sha256: '',
        confirm_password_update_sha256: '',
      });
    },
    onResetFail: (err: unknown) => {
      const error = err as { message?: string };
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      new NoticeMessage(t(error?.message || 'An error occurred'));
    },
  });

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;

    setFormData((prev) => {
      const updatedFormData = { ...prev, [name]: value };

      const fieldsToCheck = [
        updatedFormData.current_password,
        updatedFormData.no_hash_password_update,
        updatedFormData.no_hash_confirm_password_update,
      ];

      const isFormEmpty = fieldsToCheck.every((val) => !val?.trim());

      setIsDisabled(isFormEmpty);
      setIsRequired(!isFormEmpty);

      return updatedFormData;
    });
  };

  const handleSubmit = async () => {
    const updatedFormData: FormData = {
      ...formData,
      current_password_sha256: Common.SHA256(formData.current_password.trim()),
      confirm_password_update_sha256: Common.SHA256(
        formData.no_hash_confirm_password_update.trim()
      ),
    };

    console.log('Form Data to Send:', updatedFormData);
    updateProfilePassword(updatedFormData as unknown as PasswordUpdateData);
  };

  const tabClass = isActive
    ? 'flex flex-col pt-[20px] bg-transparent rounded-[4px] bg-[#135a78]'
    : 'pt-[20px]';

  return (
    <div className={tabClass}>
      <div className="w-full flex flex-col gap-[15px]">
        <span className="text-[20px] font-[700] text-[#6b7280] dark:text-text-white">
          {t('cmn > user password')}
        </span>
      </div>
      <div className="flex flex-col gap-4 mt-4">
        <DetailModal
          label={t('user > current password')}
          inputType="password"
          name="current_password"
          modalType="profile"
          required={isRequired}
          value={formData.current_password}
          onChange={handleChange}
        />
        <DetailModal
          label={t('profile > new password')}
          inputType="password"
          name="no_hash_password_update"
          modalType="profile"
          required={isRequired}
          value={formData.no_hash_password_update}
          onChange={handleChange}
        />
        <DetailModal
          label={t('profile > confirm password')}
          inputType="password"
          name="no_hash_confirm_password_update"
          modalType="profile"
          required={isRequired}
          value={formData.no_hash_confirm_password_update}
          onChange={handleChange}
        />
      </div>
      <div className="flex justify-end mt-12">
        <Button
          label={t('cmn > confirm')}
          customButton="btn-search text-[14px]! h-[36px]!"
          disabled={isDisabled}
          onClick={handleSubmit}
        />
      </div>
    </div>
  );
};

export default UpdatePassword;
